import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { WorkerTableRow } from '../../roster/index.js'
import { ADMIN_API_BASE_URL, EndpointHint, Skeleton } from '../../core/index.js'
import { getWorkerStanding, type PartnerSession } from '../lib/partnerApi.js'
import { StandingCard } from '../../scoring/index.js'
import { ErrorNote, INPUT_CLASS, Labeled, describeError } from './formControls.js'

/**
 * Fetches `GET /workers/{workerId}/standing` as the partner and renders it
 * with the same card the worker app uses - what a partner would build into
 * its own worker screen from this one response.
 */
export function StandingView({ session, workers }: { session: PartnerSession; workers: WorkerTableRow[] }) {
  const [workerId, setWorkerId] = useState('')

  const standingQuery = useQuery({
    queryKey: ['partner-simulator', 'standing', session.clientId, workerId],
    queryFn: () => getWorkerStanding(ADMIN_API_BASE_URL ?? '', session, workerId),
    enabled: workerId !== '' && Boolean(ADMIN_API_BASE_URL),
    // Always the live answer: a tier override or config change made in another tab should show on the next look.
    staleTime: 0,
  })

  return (
    <div className="space-y-4">
      <div className="grid gap-5 sm:grid-cols-2">
        <Labeled label="Worker" htmlFor="standing-worker" hint={workers.length === 0 ? 'This facility has no workers on its roster yet.' : undefined}>
          <select id="standing-worker" className={INPUT_CLASS} value={workerId} onChange={(event) => setWorkerId(event.target.value)}>
            <option value="">Choose a worker…</option>
            {workers.map((worker) => (
              <option key={worker.workerId} value={worker.workerId}>
                {worker.name} ({worker.workerId})
              </option>
            ))}
          </select>
        </Labeled>
      </div>

      {workerId !== '' && (
        <>
          <EndpointHint className="block">GET /workers/{workerId}/standing</EndpointHint>
          {standingQuery.isPending ? (
            <Skeleton label="Loading standing" />
          ) : standingQuery.isError ? (
            <ErrorNote message={describeError(standingQuery.error, 'Could not load this worker’s standing.')} onRetry={() => void standingQuery.refetch()} retrying={standingQuery.isFetching} />
          ) : (
            <div className="grid gap-5 md:grid-cols-2 items-start">
              <div className="bg-canvas border border-marine/15 p-4">
                <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-2">As a worker screen</div>
                <StandingCard standing={standingQuery.data} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-2">The response it was built from</div>
                <pre className="bg-canvas border border-marine/15 p-3 text-[11px] font-mono text-ink whitespace-pre-wrap break-all">
                  {JSON.stringify(standingQuery.data, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
